'use client';

import type { UseQueryResult } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import useProductsInventory, {
  DEFAULT_PRODUCTS_INVENTORY,
  type UseProductsInventory,
} from '@/app/[locale]/product/_hooks/useProductsInventory';
import { useCartContext } from '@/app/contexts/CartProvider';
import logToDatadog from '@/logger/logToDatadog';
import { useProductsData } from '@/queries/Products';
import type { ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';

interface PDPValues extends UseProductsInventory {
  data?: ListProduct[];
  error: unknown;
  isLoadingPdpData: boolean;
  isSuccess: boolean;
}

export const INITIAL_PDP_VALUES: PDPValues = {
  data: [],
  error: null,
  isLoadingPdpData: true,
  isSuccess: false,
  ...DEFAULT_PRODUCTS_INVENTORY,
};

type PDPProvider = PDPValues & UseQueryResult<ListProduct[], Error>;
export type PDPContextType =
  | (PDPProvider & UseProductsInventory)
  | (PDPValues & UseProductsInventory);

const PDPContext = createContext<PDPContextType>(INITIAL_PDP_VALUES);

export const usePDPContext = (): PDPContextType => {
  return useContext(PDPContext);
};

const Provider = ({ children }: PropsWithChildren) => {
  const inventory = useProductsInventory();
  // get cart
  const { refreshCartError, refreshCartLoading } = useCartContext();

  if (refreshCartError) {
    logToDatadog({
      error: refreshCartError,
      message: 'Error fetching cart in PDPProvider',
      module: 'cart',
    });
  }

  // get products
  const products = useProductsData({
    includeInventory: true,
  });

  const isLoadingPdpData = refreshCartLoading || products.isLoading;

  const values: PDPProvider = useMemo(() => {
    return {
      isLoadingPdpData,
      ...products,
      ...inventory,
    };
  }, [inventory, isLoadingPdpData, products]);

  return <PDPContext.Provider value={values}>{children}</PDPContext.Provider>;
};

export default Provider;
